import React, { useState, useEffect } from 'react';
import { Typography, Container, Box, Grid, Card, CardContent, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem, Button, useMediaQuery, useTheme, Stack } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { getAccounts, getAccountAnalytics } from '../services/api';
import { Link } from 'react-router-dom';

const COLORS = ['#8884D8', '#82CA9D', '#FFC658', '#FF7C7C', '#8DD1E1', '#D084D0'];

function AccountAnalyticsPage() {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chartView, setChartView] = useState('all');

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      fetchAccountData();
    }
  }, [selectedAccount]);

  const fetchAccounts = async () => {
    try {
      const response = await getAccounts();
      setAccounts(response.data);
      if (response.data.length > 0) {
        setSelectedAccount(response.data[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch accounts', err);
      setError('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountData = async () => {
    try {
      setLoading(true);
      const response = await getAccountAnalytics(selectedAccount);
      setData(response.data);
    } catch (err) {
      console.error('Failed to fetch account analytics', err);
      setError('Failed to load account analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Account Performance Analysis
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Button 
            variant="outlined"
            component={Link} 
            to="/analytics"
            sx={{ mr: 1 }}
          >
            Overview
          </Button>
          <Button 
            variant="outlined"
            component={Link} 
            to="/analytics/categories"
            sx={{ mr: 1 }}
          >
            Category Insights
          </Button>
          <Button 
            variant="contained"
            sx={{ mr: 1 }}
          >
            Account Performance
          </Button>
        </Box>

        <Box sx={{ 
  mb: 3, 
  display: 'flex', 
  gap: { xs: 1, md: 2 },
  flexWrap: { xs: 'wrap', md: 'nowrap' },
  justifyContent: { xs: 'center', md: 'flex-start' }
}}>
  <Button
    variant={chartView === 'balance' ? "contained" : "outlined"}
    onClick={() => setChartView('balance')}
    size={isMobile ? "small" : "medium"}
    sx={{ 
      minHeight: { xs: 40, md: 36 },
      flex: { xs: '1 1 auto', md: '0 0 auto' },
      minWidth: { xs: 100, md: 'auto' }
    }}
  >
    Balance Trend
  </Button>
  <Button
    variant={chartView === 'categories' ? "contained" : "outlined"}
    onClick={() => setChartView('categories')}
    size={isMobile ? "small" : "medium"}
    sx={{ 
      minHeight: { xs: 40, md: 36 },
      flex: { xs: '1 1 auto', md: '0 0 auto' },
      minWidth: { xs: 100, md: 'auto' }
    }}
  >
    Categories
  </Button>
  <Button
    variant={chartView === 'frequency' ? "contained" : "outlined"}
    onClick={() => setChartView('frequency')}
    size={isMobile ? "small" : "medium"}
    sx={{ 
      minHeight: { xs: 40, md: 36 },
      flex: { xs: '1 1 auto', md: '0 0 auto' },
      minWidth: { xs: 100, md: 'auto' }
    }}
  >
    Frequency
  </Button>
</Box>

        <FormControl 
          variant="outlined" 
          sx={{ 
            minWidth: { xs: '100%', sm: 200 }, 
            mb: { xs: 2, md: 3 } 
          }}
          fullWidth={isMobile}
        >
          <InputLabel>Select Account</InputLabel>
          <Select
            value={selectedAccount}
            label="Select Account"
            onChange={(e) => setSelectedAccount(e.target.value)}
          >
            {accounts.map((account) => (
              <MenuItem key={account.id} value={account.id}>
                {account.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {data && (
          <>
            {chartView === 'all' ? (
              <Grid container spacing={{ xs: 2, md: 3 }}>
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {data.account_name} - Balance History (90 Days)
                      </Typography>
                      <ResponsiveContainer width="100%" height={isMobile ? 250 : 400}>
                        <LineChart data={data.balance_history}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="date" 
                            tickFormatter={(value) => new Date(value).toLocaleDateString()}
                          />
                          <YAxis tickFormatter={(value) => formatCurrency(value)} />
                          <Tooltip 
                            labelFormatter={(value) => new Date(value).toLocaleDateString()}
                            formatter={(value) => [formatCurrency(value), 'Balance']}
                          />
                          <Legend />
                          <Line 
                            type="monotone" 
                            dataKey="balance" 
                            stroke="#3F51B5" 
                            strokeWidth={3}
                            name="Account Balance"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Category Activity (Last 3 Months)
                      </Typography>
                      <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                        <BarChart data={data.category_breakdown}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="category" />
                          <YAxis tickFormatter={(value) => formatCurrency(value)} />
                          <Tooltip formatter={(value) => [formatCurrency(value), 'Total']} />
                          <Bar 
                            dataKey="amount" 
                            fill="#9C27B0"
                            name="Category Total"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Monthly Transaction Count
                      </Typography>
                      <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                        <BarChart data={data.transaction_frequency}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="count" fill="#FF9800" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              <Box sx={{ width: '100%' }}>
                {chartView === 'balance' && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {data.account_name} - Balance History (90 Days)
                      </Typography>
                      <ResponsiveContainer width="100%" height={600}>
                        <LineChart data={data.balance_history}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="date" 
                            tickFormatter={(value) => new Date(value).toLocaleDateString()}
                          />
                          <YAxis tickFormatter={(value) => formatCurrency(value)} />
                          <Tooltip 
                            labelFormatter={(value) => new Date(value).toLocaleDateString()}
                            formatter={(value) => [formatCurrency(value), 'Balance']}
                          />
                          <Legend />
                          <Line 
                            type="monotone" 
                            dataKey="balance" 
                            stroke="#3F51B5" 
                            strokeWidth={4}
                            name="Account Balance"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {chartView === 'categories' && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Category Activity (Last 3 Months)
                      </Typography>
                      <ResponsiveContainer width="100%" height={600}>
                        <BarChart data={data.category_breakdown}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="category" />
                          <YAxis tickFormatter={(value) => formatCurrency(value)} />
                          <Tooltip formatter={(value) => [formatCurrency(value), 'Total']} />
                          <Bar 
                            dataKey="amount" 
                            fill="#9C27B0"
                            name="Category Total"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {chartView === 'frequency' && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Monthly Transaction Count
                      </Typography>
                      <ResponsiveContainer width="100%" height={600}>
                        <BarChart data={data.transaction_frequency}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="count" fill="#FF9800" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Container>
  );
}

export default AccountAnalyticsPage;